/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * Investor Decision Resolver
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Maps investor notifications into normalized InvestorDecisionItem entries.
 * Determines decision type, status, urgency, panel targets, and CTA labels.
 *
 * @module lib/investor/routing/decisionResolver
 */

import type { InvestorNotification, InvestorNotificationType } from '@/types/investorNotifications';
import type {
    InvestorDecisionItem,
    InvestorDecisionType,
    InvestorDecisionStatus,
    InvestorDecisionUrgency,
} from '@/types/investorDecisions';
import type { InvestorPanelType, InvestorEntityType } from './types';
import { EVENT_TYPE_META, URGENCY_LEVELS } from './taxonomy';

// ═══════════════════════════════════════════════════════════════════════════════
// NOTIFICATION → DECISION MAPPING
// ═══════════════════════════════════════════════════════════════════════════════

interface DecisionMapping {
    decisionType: InvestorDecisionType;
    panelType: InvestorPanelType;
    entityType: InvestorEntityType;
    primaryCtaLabel: string;
    secondaryCtaLabel?: string;
    /** Determines the decision status from a notification. */
    resolveStatus: (n: InvestorNotification) => InvestorDecisionStatus;
    /** Build the decision title. */
    resolveTitle: (n: InvestorNotification) => string;
    /** Build the decision summary. */
    resolveSummary: (n: InvestorNotification) => string;
}

/**
 * Notification types that produce decision items, and their terminal/completed counterparts.
 */
const DECISION_TYPE_MAP: Partial<Record<InvestorNotificationType, DecisionMapping>> = {
    // ── Signatures ───────────────────────────────────────────────────────
    signature_requested: {
        decisionType: 'signature',
        panelType: 'signature-review',
        entityType: 'signature_request',
        primaryCtaLabel: 'Review & Sign',
        resolveStatus: () => 'action_required',
        resolveTitle: (n) => n.title || 'Signature Required',
        resolveSummary: (n) => n.message || 'Your signature is required to finalize commitment documents.',
    },
    signature_completed: {
        decisionType: 'signature',
        panelType: 'signature-review',
        entityType: 'signature_request',
        primaryCtaLabel: 'View Document',
        resolveStatus: () => 'completed',
        resolveTitle: (n) => n.title || 'Signature Completed',
        resolveSummary: (n) => n.message || 'Document has been fully signed by all parties.',
    },

    // ── Finance Approvals ────────────────────────────────────────────────
    finance_approval_requested: {
        decisionType: 'finance_approval',
        panelType: 'finance-review',
        entityType: 'finance_approval',
        primaryCtaLabel: 'Review Request',
        resolveStatus: () => 'action_required',
        resolveTitle: (n) => n.title || 'Finance Approval Requested',
        resolveSummary: (n) => n.message || 'A capital release request is awaiting investor decision.',
    },
    finance_approval_decided: {
        decisionType: 'finance_approval',
        panelType: 'finance-review',
        entityType: 'finance_approval',
        primaryCtaLabel: 'View Decision',
        resolveStatus: () => 'completed',
        resolveTitle: (n) => n.title || 'Finance Approval Decided',
        resolveSummary: (n) => n.message || 'A finance approval decision has been made.',
    },

    // ── Milestones ───────────────────────────────────────────────────────
    milestone_verified: {
        decisionType: 'milestone_review',
        panelType: 'milestone-review',
        entityType: 'milestone',
        primaryCtaLabel: 'Review Milestone',
        resolveStatus: () => 'action_required',
        resolveTitle: (n) => n.title || 'Milestone Review',
        resolveSummary: (n) => n.message || 'Verification evidence is available for milestone review.',
    },

    // ── Evidence ─────────────────────────────────────────────────────────
    evidence_available: {
        decisionType: 'evidence_review',
        panelType: 'evidence-preview',
        entityType: 'evidence_pack',
        primaryCtaLabel: 'Review Evidence',
        resolveStatus: () => 'action_required',
        resolveTitle: (n) => n.title || 'Evidence Available for Review',
        resolveSummary: (n) => n.message || 'New verification documentation is available for review.',
    },

    // ── Approvals (read-only for investors — drawer is view-only) ────────
    approval_requested: {
        decisionType: 'approval',
        panelType: 'approval-review',
        entityType: 'approval_workflow',
        primaryCtaLabel: 'View Approval Status',
        resolveStatus: () => 'waiting',
        resolveTitle: (n) => n.title || 'Approval In Progress',
        resolveSummary: (n) => n.message || 'An approval workflow is in progress. Investor review is informational.',
    },
    approval_decided: {
        decisionType: 'approval',
        panelType: 'approval-review',
        entityType: 'approval_workflow',
        primaryCtaLabel: 'View Decision',
        resolveStatus: () => 'completed',
        resolveTitle: (n) => n.title || 'Approval Decided',
        resolveSummary: (n) => n.message || 'An approval decision has been made.',
    },

    // ── Distributions ────────────────────────────────────────────────────
    distribution_pending: {
        decisionType: 'distribution_review',
        panelType: 'distribution-detail',
        entityType: 'distribution',
        primaryCtaLabel: 'Review Distribution',
        resolveStatus: () => 'waiting',
        resolveTitle: (n) => n.title || 'Distribution Pending',
        resolveSummary: (n) => n.message || 'A distribution is being processed.',
    },
};

// ═══════════════════════════════════════════════════════════════════════════════
// RESOLVER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Convert a notification to an InvestorDecisionItem, if applicable.
 * Returns null for notification types that don't produce decisions.
 */
export function resolveNotificationToDecision(
    notification: InvestorNotification,
): InvestorDecisionItem | null {
    const mapping = DECISION_TYPE_MAP[notification.type];
    if (!mapping) return null;

    const eventMeta = EVENT_TYPE_META[notification.type];
    const urgency = resolveUrgency(eventMeta.priority);

    return {
        id: `decision-${notification.id}`,
        type: mapping.decisionType,
        title: mapping.resolveTitle(notification),
        summary: mapping.resolveSummary(notification),
        projectId: notification.projectId,
        projectName: notification.projectName,
        entityId: notification.referenceId ?? notification.id,
        entityType: mapping.entityType,
        panelType: mapping.panelType,
        urgency,
        status: mapping.resolveStatus(notification),
        createdAt: notification.createdAt,
        completedAt: mapping.resolveStatus(notification) === 'completed' ? notification.createdAt : undefined,
        primaryCtaLabel: mapping.primaryCtaLabel,
        secondaryCtaLabel: mapping.secondaryCtaLabel,
        sourceNotificationId: notification.id,
    };
}

/**
 * Convert a batch of notifications into a sorted decision queue.
 */
export function buildDecisionQueue(
    notifications: InvestorNotification[],
): InvestorDecisionItem[] {
    const items: InvestorDecisionItem[] = [];

    for (const n of notifications) {
        const decision = resolveNotificationToDecision(n);
        if (decision) {
            items.push(decision);
        }
    }

    // Sort: action_required first, then by urgency, then by date
    return items.sort((a, b) => {
        const statusOrder: Record<InvestorDecisionStatus, number> = {
            action_required: 0,
            waiting: 1,
            completed: 2,
        };
        const urgencyOrder: Record<InvestorDecisionUrgency, number> = {
            critical: 0,
            high: 1,
            medium: 2,
            low: 3,
        };

        // Status first
        const statusDiff = statusOrder[a.status] - statusOrder[b.status];
        if (statusDiff !== 0) return statusDiff;

        // Then urgency
        const urgencyDiff = urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
        if (urgencyDiff !== 0) return urgencyDiff;

        // Then newest first
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function resolveUrgency(priority: number): InvestorDecisionUrgency {
    const level = URGENCY_LEVELS.find(u => priority >= u.minPriority);
    return level?.level ?? 'low';
}

/** Notification types that produce decision queue items. */
export const DECISION_NOTIFICATION_TYPES = new Set(
    Object.keys(DECISION_TYPE_MAP) as InvestorNotificationType[],
);
